import { Cesium } from 'app/cesium';
import { IMapInputHandler } from 'app/map/services/map_input_handling/MapInputHandler';
import { MapService } from 'app/map/services/map.service';
import { MapInputHandlerCollection } from 'app/map/services/map_input_handling/map_input_handler_collection';
import { CesiumMapInputHandlerCollection } from 'app/map/services/map_input_handling/cesium_map_input_handler_collection';

export const CESIUM_INPUT_EVENT_MAP = {
    leftDown: Cesium.ScreenSpaceEventType.LEFT_DOWN,
    leftUp: Cesium.ScreenSpaceEventType.LEFT_UP,
    leftClick: Cesium.ScreenSpaceEventType.LEFT_CLICK,
    leftDoubleClick: Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK,
    rightDown: Cesium.ScreenSpaceEventType.RIGHT_DOWN,
    rightUp: Cesium.ScreenSpaceEventType.RIGHT_UP,
    rightClick: Cesium.ScreenSpaceEventType.RIGHT_CLICK,
    middleDown: Cesium.ScreenSpaceEventType.MIDDLE_DOWN,
    middleUp: Cesium.ScreenSpaceEventType.MIDDLE_UP,
    middleClick: Cesium.ScreenSpaceEventType.MIDDLE_CLICK,
    mouseMove: Cesium.ScreenSpaceEventType.MOUSE_MOVE,
    wheel: Cesium.ScreenSpaceEventType.WHEEL,
    pinchStart: Cesium.ScreenSpaceEventType.PINCH_START,
    pinchEnd: Cesium.ScreenSpaceEventType.PINCH_END,
    pinchMove: Cesium.ScreenSpaceEventType.PINCH_MOVE,
};

export class MapInputHandlerService {
    private _viewer;
    private _mapService: MapService;
    private _mapInputHandlerCollection: MapInputHandlerCollection;
    private _cesiumMapInputHandlerCollection: CesiumMapInputHandlerCollection;

    constructor(
        mapService: MapService,
        mapInputHandlerCollection: MapInputHandlerCollection,
        cesiumMapInputHandlerCollection: CesiumMapInputHandlerCollection,
    ) {
        this._viewer = undefined;
        this._cesiumMapInputHandlerCollection = cesiumMapInputHandlerCollection;

        this._mapService = mapService;
        this._mapInputHandlerCollection = mapInputHandlerCollection;

        this._mapInputHandlerCollection.subscribeToCleanupCalls(async () => {
            await this.setDefaultHandler();
        });

        mapService.addOnViewerReadyListener(this._initialise.bind(this));
    }

    private async _initialise(viewer: Cesium.Viewer): Promise<void> {
        this._viewer = viewer;
        await this.setDefaultHandler();
    }

    public onActiveHandlerChange(callback: (handler: IMapInputHandler) => void): () => void {
        return this._mapInputHandlerCollection.subscribeToActiveHandlerChange(callback);
    }

    get isDefaultMode(): boolean {
        return (
            this._mapInputHandlerCollection.getActiveHandler().name ===
            this._mapInputHandlerCollection.getDefaultHandler().name
        );
    }

    get defaultHandler(): IMapInputHandler {
        return this._mapInputHandlerCollection.getDefaultHandler();
    }

    public isHandlerActive(handler: IMapInputHandler): boolean {
        return this._mapInputHandlerCollection.getActiveHandler() === handler;
    }

    public async setDefaultHandler(): Promise<void> {
        await this.setActiveHandler(this.defaultHandler);
    }

    public async setActiveHandler(mapInputHandler: IMapInputHandler): Promise<void> {
        if (!this._viewer) {
            throw new Error(
                "MapInputHandlerService is waiting for the viewer still, can't set input handler",
            );
        }

        this._cesiumMapInputHandlerCollection.activateCesiumMapHandler(
            mapInputHandler,
            this._viewer,
        );

        await this._mapInputHandlerCollection.setActiveHandler(mapInputHandler);
    }

    public async setActiveHandlerByName(handlerName: string): Promise<void> {
        const handler = this._mapInputHandlerCollection.getHandlerByName(handlerName);
        await this.setActiveHandler(handler);
    }

    public addMapInputHandler(mapInputHandler: IMapInputHandler): void {
        this._mapInputHandlerCollection.addMapInputHandler(mapInputHandler);
        this._cesiumMapInputHandlerCollection.addCesiumMapHandler(mapInputHandler);
    }

    public removeMapInputHandler(mapInputHandler: IMapInputHandler): void {
        this._mapInputHandlerCollection.removeMapInputHandler(mapInputHandler);
        this._cesiumMapInputHandlerCollection.removeCesiumMapHandler(mapInputHandler);
    }
}
